import React, { useCallback } from "react";
import { SpaceProps } from "styled-system";
import { Flex } from "../../../../Flex";
import { Field, useFormikContext } from "formik";
import { FormikRadioButton } from "../../../../FormikRadioButton";
import { ContactDetails } from "../../../../../types/ContactDetailsValues";
import { ContactConfig } from "../../../../../types/ContactConfig";
import {
  assignSelectedRoleToOtherContact,
  hasRoleBeenAssignedToContact,
  isContactAssignable,
  unassignSelectedRoleFromOtherContacts,
} from "../utils/AssignedRolesUtils";
import kebabCase from "lodash/kebabCase";
import {
  AssignedRoles,
  RoleLabel,
} from "../../../../../types/ContactDetailRoles";

interface Props {
  idPrefix: string;
  namePrefix: string;
  contactIndex: number;
  contactConfigs: ContactConfig[];
  contactDetails: ContactDetails[];
}

export const AssignRoleRadioButtonGroup: React.FC<Props & SpaceProps> = ({
  idPrefix,
  namePrefix,
  contactIndex,
  contactConfigs,
  contactDetails,
}) => {
  const contact = contactDetails[contactIndex];
  const { setFieldValue } = useFormikContext();

  const setAssignedRolesFieldValue = useCallback(
    (index: number, roles: AssignedRoles) => {
      setFieldValue(`${namePrefix}[${index}].roles`, roles);
    },
    [namePrefix, setFieldValue],
  );

  const handleOnChange = useCallback(
    (selectedRole: RoleLabel) => {
      assignSelectedRoleToOtherContact(
        contact.role,
        selectedRole,
        contactDetails,
        setAssignedRolesFieldValue,
      );
      unassignSelectedRoleFromOtherContacts(
        contact.role,
        selectedRole,
        contactDetails,
        setAssignedRolesFieldValue,
      );
    },
    [contact, contactDetails, setAssignedRolesFieldValue],
  );

  return (
    <Flex flexDirection={["column", "column", "row"]} flexWrap={"wrap"}>
      {contactConfigs.map(
        (contactConfig, index: number) =>
          index !== contactIndex && (
            <Flex
              key={`${idPrefix}.radio[${index}]`}
              flexDirection="column"
              flexGrow={0.5}
              flexBasis={"35%"}
              mr={[0, 0, 3]}
              mb={[3, 3, 3]}
            >
              <Field
                id={`${idPrefix}-to-${kebabCase(contactConfig.role)}`}
                magnitude="small"
                label={`Same as ${contactConfig.role.toLowerCase()}`}
                name={`${namePrefix}[${contactIndex}].sameAsRole`}
                value={contactConfig.role}
                currentValue={contact.sameAsRole}
                flexGrow={0.5}
                flexBasis={0}
                minWidth={0}
                useTimeout
                onChange={() => {
                  handleOnChange(contactConfig.role);
                }}
                component={FormikRadioButton}
                disabled={
                  !isContactAssignable(contactDetails[index]) ||
                  hasRoleBeenAssignedToContact(
                    contactDetails[contactIndex].role,
                    contactDetails,
                  )
                }
              />
            </Flex>
          ),
      )}
      <Flex
        flexDirection="column"
        flexGrow={0.5}
        flexBasis={"35%"}
        mr={[0, 0, 3]}
        mb={[3, 3, 3]}
      >
        <Field
          id={`${idPrefix}-to-new-contact`}
          magnitude="small"
          label="New contact"
          name={`${namePrefix}[${contactIndex}].sameAsRole`}
          value={contact.role}
          currentValue={contact.sameAsRole}
          flexGrow={0.5}
          flexBasis={0}
          minWidth={0}
          useTimeout
          onChange={() => handleOnChange(contact.role)}
          component={FormikRadioButton}
        />
      </Flex>
    </Flex>
  );
};
